import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SearchInput from '../SearchPage/SearchInput';
import * as searchTypes from '../../../constants/searchTypes';
import ShipstationShopIcon from '../ShipstationShopIcon/ShipstationShopIcon';
import Loading from '../../LoadingComponent/LoadingComponent';
import isEmpty from 'lodash/isEmpty';
import {
  Wrapper,
  PageHeader,
  BackButton,
  Header,
  HeaderDescription,
  SearchSection,
  SearchByLetterBar,
  SearchByLetterLabel,
  SearchLetter,
  SearchResultsWrapper,
  SearchResultsLabel,
  SearchResults,
  SearchResult,
  ContinueButton,
  LoadingContainer,
} from './ShopSelect.styles';
import { handleError } from 'services/ErrorService/ErrorService';

const SHOPIFY_DOMAIN = '.myshopify.com';
const ALLOWED_HOSTS = ['zendesk.com'];

class ShopSelect extends Component {
  constructor(props) {
    super(props);
    this.searchFieldRef = null;

    const originalShopNames = Object.keys(props.shopifyConfigs);
    const availableShops = originalShopNames.map((shop) => shop.replace(SHOPIFY_DOMAIN, ''));
    const numberOfShops = availableShops.length;
    availableShops.sort();
    const availableLetters = [...new Set(availableShops.map((shop) => shop.charAt(0).toUpperCase()))];
    const allLetters = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase().split('');

    let listedShops = [];
    if (numberOfShops <= 9) {
      listedShops = availableShops;
    }

    this.state = {
      originalShopNames,
      searchPerformed: false,
      listedShops,
      selectedLetter: '',
      availableShops,
      allLetters,
      availableLetters,
      selectedShop: '',
      numberOfShops,
      shipstationShops: [],
      showSelectionScreen: false,
    };

    this._isMounted = false;
  }

  async componentDidMount() {
    this._isMounted = true;

    try {
      const shops = await this.props.loadShipstationShops();
      if (this._isMounted) {
        this.setState({ shipstationShops: shops });
      }
    } catch (error) {
      handleError({ error, customMessage: 'ShopSelect.componentDidMount: loadShipstationShops threw an exception' });
    }

    const showSelectionScreen = !this.autoSelectSearchOrderShops();
    this.setState({ showSelectionScreen });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  autoSelectSearchOrderShops = () => {
    const originalShopNames = this.state.originalShopNames;
    if (originalShopNames.length) {
      if (originalShopNames.length === 1) {
        this.props.selectSearchOrderShops(
          originalShopNames,
          this.props.isZendeskShopSelected ? this.props.shopifyConfigs : null
        );

        return true;
      } else if (!this.props.isZendeskShopSelected && this.props.ticket) {
        /*
        There are two types of Shopify stores:
        One that is served off the domain 'myshopify.com' and another that is served off the domain 'zendesk.com'.
        The second type of stores' order query functions are not yet setup to query on multiple stores.
        Therefore for the second type of these stores, auto-select a store only if there is only *one* store.
        */
        if (!this.props.ticket.brand) {
          if (originalShopNames.length <= 3) {
            this.props.selectSearchOrderShops(originalShopNames, null);

            return true;
          }
        } else {
          let selectedShops = [];

          selectedShops = originalShopNames.filter((originalShopName) => {
            const shopConfig = this.props.shopifyConfigs[originalShopName];
            if (!shopConfig) {
              return false;
            }

            const addStoreToSearch =
              isEmpty(shopConfig.brands) ||
              (Array.isArray(shopConfig.brands) && shopConfig.brands.includes(this.props.ticket.brand));

            return addStoreToSearch;
          });

          // If more then 3 potential stores, make the expert choose one explicitely on the UI.
          // Otherwise, auto select these stores for a search:
          if (selectedShops.length >= 1 && selectedShops.length <= 3) {
            this.props.selectSearchOrderShops(selectedShops, null);

            return true;
          }
        }
      }
    }

    return false;
  };

  handleSearchClick(value) {
    let listedShops = this.state.availableShops.filter((shop) => shop.toLowerCase().includes(value.toLowerCase()));
    this.setState({ selectedLetter: '', selectedShop: '', listedShops: listedShops, searchPerformed: true });
  }

  handleLetterClick(letter) {
    let listedShops = this.state.availableShops.filter((shop) => shop.charAt(0).toUpperCase() === letter);
    this.setState({ selectedLetter: letter, selectedShop: '', listedShops: listedShops, searchPerformed: true });

    this.searchFieldRef && this.searchFieldRef.setSearchValue('');
  }

  handleShopClick(shop) {
    if (this.state.selectedShop === shop) {
      this.setState({ selectedShop: '' });
      this.props.deselectSearchOrderShops();
    } else {
      this.setState({ selectedShop: shop });
    }
  }

  handleContinueClick() {
    try {
      const url = new URL(this.state.selectedShop);
      const hostname = url.hostname;

      const selectedShop = ALLOWED_HOSTS.includes(hostname)
        ? this.state.selectedShop
        : this.state.selectedShop + SHOPIFY_DOMAIN;

      this.props.selectSearchOrderShops([selectedShop]);
    } catch (e) {
      handleError({ error: e, customMessage: 'Invalid URL in handleContinueClick' });
    }
  }

  handleBackButton() {
    this.props.setSearchType(searchTypes.SEARCH_ALL);
  }

  searchRequired() {
    return this.state.numberOfShops > 9;
  }

  get smbName() {
    const business = this.props.business;

    return business && business.smbName;
  }

  render() {
    if (!this.state.showSelectionScreen) {
      return (
        <LoadingContainer>
          <Loading delay={0} label="Loading" marginTop={'0px'} />
        </LoadingContainer>
      );
    }

    let lettersJSX = this.state.allLetters.map((letter) => {
      let letterIsActive = this.state.availableLetters.includes(letter);

      return (
        <SearchLetter
          key={`letter-${letter}`}
          active={letterIsActive}
          selected={this.state.selectedLetter === letter}
          onClick={() => letterIsActive && this.handleLetterClick(letter)}
        >
          {letter}
        </SearchLetter>
      );
    });

    let shopsJSX = this.state.listedShops.map((shop) => {
      const fullShopName = shop + SHOPIFY_DOMAIN;
      const hasShipstation = this.state.shipstationShops.indexOf(fullShopName) !== -1;

      return (
        <SearchResult
          key={`shopify-shop-search-shop-${fullShopName}`}
          onClick={() => this.handleShopClick(shop)}
          active={this.state.selectedShop === shop}
          hasShipstation={hasShipstation}
        >
          {shop}
          {hasShipstation && <ShipstationShopIcon />}
        </SearchResult>
      );
    });

    return (
      <Wrapper className="widget">
        <PageHeader>
          <BackButton
            onClick={() => {
              this.handleBackButton();
            }}
          >
            <img src="/images/curved-back-arrow.svg" alt="back-button" />
          </BackButton>
        </PageHeader>

        {this.searchRequired() && (
          <div>
            {this.smbName === 'Future Shirts' ? (
              <div>
                <Header>
                  To find the corresponding store, select the first letter of the band, artist, or group name.
                </Header>
                <HeaderDescription>
                  Hint: Use order number as a guide (ex. RLYGRN#1234 is Riley Green’s store). Please ask the customer
                  the name of the store inquiry and then choose the equivalent one from the search tool below. If the
                  name is not found, please proceed with escalating this ticket.
                </HeaderDescription>
              </div>
            ) : (
              <Header>
                This partner has multiple shopify stores, please select the correct store name to find the order.
              </Header>
            )}

            <SearchByLetterLabel>Search by letter</SearchByLetterLabel>
            <SearchSection>
              <SearchByLetterBar>{lettersJSX}</SearchByLetterBar>
              <SearchInput
                ref={(el) => (this.searchFieldRef = el)}
                defaultValue={''}
                messageImg={false}
                placeholder={'Enter a name'}
                onClickFunction={(value) => this.handleSearchClick(value)}
              />
            </SearchSection>
          </div>
        )}

        {
          // eslint-disable-next-line
          this.state.searchPerformed || !this.searchRequired() ? (
            this.state.listedShops.length ? (
              <SearchResultsWrapper>
                {this.state.selectedLetter || !this.searchRequired() ? (
                  <SearchResultsLabel>Please choose the store name and press continue</SearchResultsLabel>
                ) : (
                  <SearchResultsLabel>The store name was found, please select and continue</SearchResultsLabel>
                )}

                <SearchResults>{shopsJSX}</SearchResults>
                <ContinueButton
                  fullColored={true}
                  active={this.state.selectedShop}
                  onClick={() => this.state.selectedShop && this.handleContinueClick()}
                >
                  Continue
                </ContinueButton>
              </SearchResultsWrapper>
            ) : (
              <SearchResultsWrapper>
                <SearchResultsLabel>Sorry but no matching name was found, please try again.</SearchResultsLabel>
              </SearchResultsWrapper>
            )
          ) : (
            ''
          )
        }
      </Wrapper>
    );
  }
}

ShopSelect.propTypes = {
  shopifyConfigs: PropTypes.object.isRequired,
  selectShopAndConfig: PropTypes.func.isRequired,
  business: PropTypes.object.isRequired,
  setSearchType: PropTypes.func.isRequired,
};

export default ShopSelect;
